import { useCallback, useEffect, useRef, useState } from "react";

export type Theme = "light" | "dark";

/**
 * Theme lives on `<html data-theme>`, written by an inline script in the HTML
 * head before first paint. This hook only keeps React in step with it, so
 * there is never a flash of the wrong theme on load.
 */
export function useTheme(storageKey: string): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (document.documentElement.dataset.theme as Theme) || "dark",
  );

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      document.documentElement.dataset.theme = next;
      localStorage.setItem(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return [theme, toggle];
}

/** Value that trails its input, for search boxes that hit the API. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * Copy to clipboard.
 *
 * `navigator.clipboard` is unavailable on a page served over plain HTTP from
 * anything but localhost, which is how this panel is often reached on a LAN —
 * so a `execCommand` path stays behind it rather than the button silently
 * doing nothing.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Runs a callback on an interval, without restarting it when the callback changes. */
export function useInterval(fn: () => void, ms: number | null) {
  const saved = useRef(fn);
  saved.current = fn;
  useEffect(() => {
    if (ms == null) return;
    const id = setInterval(() => saved.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

/** Registers a document-level keyboard shortcut, inert while a field has focus. */
export function useHotkey(
  key: string,
  handler: (e: KeyboardEvent) => void,
  opts: { meta?: boolean; whileTyping?: boolean } = {},
) {
  const saved = useRef(handler);
  saved.current = handler;
  const { meta = false, whileTyping = false } = opts;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key.toLowerCase()) return;
      if (meta && !(e.metaKey || e.ctrlKey)) return;
      if (!meta && (e.metaKey || e.ctrlKey || e.altKey)) return;
      if (!whileTyping) {
        const el = document.activeElement;
        const tag = el?.tagName ?? "";
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (el as HTMLElement)?.isContentEditable) return;
      }
      saved.current(e);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [key, meta, whileTyping]);
}
